import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

const DISMISS_KEY = "oxford_wrong_site_banner_dismissed_v1";
const OLD_SITE_HOST = "cleancloud.site";

/**
 * Shown ONCE at the top of the page when a visitor arrives from the legacy
 * CleanCloud-hosted site (oxford-cleaners.cleancloud.site). Reassures the
 * customer that they're now on the official Oxford Cleaners site and points
 * them to the new scheduling flow.
 *
 * Detection:
 *   - document.referrer contains "cleancloud.site"   (incoming click)
 *   - URL has ?from=cleancloud                       (manual link override)
 *   - sessionStorage flag is set                     (persists across SPA nav)
 */
export default function WrongSiteBanner() {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    let dismissed = false;
    try {
      dismissed = !!window.localStorage.getItem(DISMISS_KEY);
    } catch {
      /* private mode */
    }
    if (dismissed) return;

    const referrer = (typeof document !== "undefined" && document.referrer) || "";
    const params = new URLSearchParams(window.location.search);
    const fromParam = (params.get("from") || "").toLowerCase();

    let triggered = false;
    try {
      triggered =
        window.sessionStorage.getItem("oxford_from_cleancloud") === "1";
    } catch {
      /* ignore */
    }

    if (referrer.includes(OLD_SITE_HOST) || fromParam === "cleancloud") {
      triggered = true;
      try {
        window.sessionStorage.setItem("oxford_from_cleancloud", "1");
      } catch {
        /* ignore */
      }
    }

    if (triggered) setShow(true);
  }, [pathname]);

  const dismiss = () => {
    setShow(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="wrong-site-banner"
      className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-brand-navy shadow-md"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-5 py-2.5 flex items-center gap-3 text-sm">
        <span className="flex-1 font-semibold leading-snug">
          You're now on the new Oxford Cleaners site —{" "}
          <span className="font-normal opacity-90">
            our old booking page is no longer active. Schedule your pickup here.
          </span>
        </span>
        <a
          href="/#schedule"
          data-testid="wrong-site-cta"
          className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-4 py-1.5 font-semibold hover:bg-slate-900 transition-colors"
        >
          Schedule pickup <ArrowRight className="h-3.5 w-3.5" />
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="wrong-site-dismiss"
          className="rounded-full p-1 hover:bg-amber-600/30 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
